/**
 * IB-27 S3, Verifier F2 — the render-path guarded-write gate.
 *
 * `ColumnDef.editable` is opt-in (design §4c), but opt-in alone is a DENYLIST-shaped protection —
 * it only refuses what a hand-maintained `GUARDED_FIELD_IDS` set (see
 * `DataGrid.guardedFieldsNeverEditable.test.tsx`) happens to enumerate, and only for the screens
 * that test happens to import. A future guarded editable column with an id outside that list, or
 * added to a screen never wired into the guard test (a new list, or QREGrid/SpendGrid gaining a
 * generic `DataGrid` in a later story), would slip through silently.
 *
 * This module flips the model to a POSITIVE allow-list, enforced by `DataGrid` itself at the
 * render path (not just by a test): a column is INERT — never enters edit mode, never renders an
 * editor, always `aria-readonly="true"` — unless its id is explicitly proven safe here, no matter
 * what `ColumnDef.editable` says. Adding a field here is a deliberate, reviewable, one-line change
 * — never automatic, never inferred from "not on a denylist."
 *
 * Add an id here ONLY after confirming ALL of:
 *  (a) it is NOT a guarded field (CLAUDE.md §2.1 — `qre_pct`/`qualifying_pct`/in-state/resident
 *      determinations/credit figures — the `AdjustmentService`-only catalog);
 *  (b) it is not itself a tax-consequential/nexus determination that belongs on a detail page with
 *      its own context (e.g. `Branch.isPermanentEstablishment` is deliberately NOT here);
 *  (c) its `editable.commit` routes through the entity's OWN existing capability-gated `PATCH`
 *      client, never `AdjustmentService`, never a raw fetch.
 */
export const EDITABLE_SAFE_FIELD_IDS: ReadonlySet<string> = new Set<string>([
  'title', // Contact.title — job title label (ContactsListScreen, `patchContact`)
  'region', // Branch.region — geographic label (BranchesListScreen, `patchBranch`)
])

/**
 * The single choke point `DataGrid` calls before EVER entering edit mode or rendering an editor
 * for a column. A column whose id is not on {@link EDITABLE_SAFE_FIELD_IDS} is treated as
 * read-only regardless of whether its `ColumnDef.editable` capability is set.
 */
export function isEditableSafeField(columnId: string): boolean {
  return EDITABLE_SAFE_FIELD_IDS.has(columnId)
}
